import { List, Map, Set } from 'immutable';
import { defaults, isFunction, keys, reduce, some } from 'lodash';

export interface IChangeField {
  key: string;
  value: any;
}

export interface IIgnoreField {
  [key: string]: boolean;
}

export function merger(a, b) {
  if (b !== null && a && a.mergeWith && !List.isList(a) && !List.isList(b)) {
    return a.mergeWith(merger, b);
  }

  return b;
}

export function serialize(model: any) {
  const isImmutable = !!(model && model.asImmutable);

  if (isImmutable) {
    return serializeHelper(
      (model.serialize ? model.serialize() : model).asMutable()
    );
  }

  return isImmutable ? model.asImmutable() : model;
}

function serializeHelper(model: Map<string, any>): Map<string, any> {
  return model.map((value) => {
    if (value && value.asImmutable) {
      if (List.isList(value)) {
        return value.map((v) => serialize(v));
      } else {
        return serialize(value);
      }
    } else {
      return value;
    }
  }) as Map<string, any>;
}

export function decorateWithKeys(model: any, modelKeys: any) {
  return defaults(model, modelKeys);
}

export function setInModel(model: Map<string, any>, changes: IChangeField[]) {
  return model.withMutations((map) => {
    changes.forEach((change: IChangeField) =>
      map.set(change.key, change.value)
    );
  });
}

export function hasValue(
  keys: any,
  defaultValues: any,
  currentValues: Map<String, any>,
  predicate?: (value: any, key: string) => boolean
) {
  if (!predicate) {
    predicate = (value: any, key: string): boolean => {
      const currentValue = currentValues.get(keys[key]);

      return !isFunction(value) && currentValue && currentValue !== value;
    };
  }

  return some(defaultValues, predicate);
}

export function keyIn(...keys) {
  const keySet = Set(keys);

  return function (v, k) {
    return keySet.has(k);
  };
}

export function generateKeys(schema: any) {
  return reduce(
    keys(schema),
    (obj, key: any) => {
      obj[key] = key.toLowerCase();

      return obj;
    },
    {}
  );
}

export function generateSchema(schema: any) {
  return reduce(
    keys(schema),
    (obj, key: any) => {
      // tsc was returning error: Cannot use 'new' with an expression whose type lacks a call or construct signature.
      // if I tried to put `schema[ key ]` in a variable first. I've tried to cast it as an `any` but it still errored out.
      try {
        // `schema[ key ]` sometimes is a class that we want to instantiate, but due to above issue,
        // we need to have this `try-catch` statements instead of a `isFunction` check
        obj[key.toLowerCase()] = new schema[key]();
      } catch (e) {
        obj[key.toLowerCase()] = schema[key];
      }

      return obj;
    },
    {}
  );
}
